/**
 * CountBucks AI Workspace — Command Center
 * =========================================
 *
 * The new top-level home: a 3-column AI workspace inspired by ChatGPT Teams /
 * Linear / Notion. Premium dark-mode SaaS dashboard.
 *
 *   ┌─────────────┬──────────────────────────────────────┬───────────────┐
 *   │ Sidebar     │  Project workspace (cover + chats)    │ Right rail    │
 *   │ - New chat  │   - Cover image                       │  - Overview   │
 *   │ - Nav       │   - Project header                    │  - Credits    │
 *   │ - Projects  │   - Tabs (Chats/Files/Knowledge/Team) │  - Top models │
 *   │ - Usage     │   - Active chat (composer + model)    │  - Shortcuts  │
 *   └─────────────┴──────────────────────────────────────┴───────────────┘
 *
 * Uses real backend:
 *   POST/GET /api/projects                  — project CRUD
 *   POST /api/projects/{id}/link-workspaces — link a project to workspaces
 *   GET /api/ai-hub/threads?project_id=...  — chats for this project
 *   POST /api/ai-hub/threads                — new chat (carries project_id)
 *   POST /api/ai-hub/threads/{id}/messages  — send message (project context inj.)
 *
 * Project financial context: when a thread is bound to a project, the AI Hub
 * backend injects a snapshot of all linked workspaces' cash position, AR, AP,
 * and 30-day revenue/expense into the system prompt automatically — so the AI
 * answers grounded in real numbers.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Sparkles, MessageSquare, FolderKanban, Files, Users, Library,
  Settings, Search, Plus, Send, Paperclip,
  ChevronDown, MoreHorizontal, Image as ImageIcon, Bell, Lock, ArrowRight,
  Bot, Pencil, Loader2, Sparkle, Activity,
  Wallet, FileText, Trash2, CheckCircle2, Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Model registry (static; future: pull from /api/ai-hub/models) ─────────
const MODELS = [
  { id: 'openai/gpt-5.2', label: 'GPT-5.2', vendor: 'OpenAI', color: '#10a37f', dot: 'bg-emerald-500' },
  { id: 'anthropic/claude-sonnet-4.5', label: 'Claude Sonnet 4.5', vendor: 'Anthropic', color: '#cc785c', dot: 'bg-amber-500' },
  { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash', vendor: 'Google', color: '#4285f4', dot: 'bg-blue-500' },
  { id: 'meta-llama/llama-3.3-70b-instruct', label: 'Llama 3.3 70B', vendor: 'Meta', color: '#0668e1', dot: 'bg-indigo-500' },
  { id: 'mistralai/mistral-large', label: 'Mistral Large', vendor: 'Mistral', color: '#fa5305', dot: 'bg-orange-500' },
  { id: 'x-ai/grok-2', label: 'Grok 2', vendor: 'xAI', color: '#1da1f2', dot: 'bg-sky-500' },
];

const COVER_PRESETS = [
  'linear-gradient(135deg, #1e1b4b 0%, #4338ca 50%, #f59e0b 100%)',
  'linear-gradient(135deg, #064e3b 0%, #10b981 50%, #34d399 100%)',
  'linear-gradient(135deg, #4c1d95 0%, #7c3aed 50%, #ec4899 100%)',
  'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #06b6d4 100%)',
  'linear-gradient(135deg, #831843 0%, #be185d 50%, #f97316 100%)',
  'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #475569 100%)',
];

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

// ── Main component ───────────────────────────────────────────────────────
export default function AIWorkspace() {
  const { workspaces } = useWorkspace();

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [composerText, setComposerText] = useState('');
  const [selectedModel, setSelectedModel] = useState('openai/gpt-5.2');
  const [activeTab, setActiveTab] = useState('chats');
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [linkWsOpen, setLinkWsOpen] = useState(false);
  const composerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // ── Data loading ───
  const fetchProjects = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_URL}/api/projects`);
      if (r.ok) {
        const list = await r.json();
        setProjects(list);
        if (!activeProjectId && list.length > 0) {
          setActiveProjectId(list[0].project_id);
        }
        // Auto-create a default project for first-time users so the workspace
        // never shows a "Create your first project" empty state — they land
        // directly in the full project workspace experience.
        if (list.length === 0) {
          const created = await apiFetch(`${API_URL}/api/projects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: 'My Workspace',
              description: 'Your default AI workspace. Link a CountBucks workspace in Settings to give the AI live access to your financials.',
              icon_color: '#10b981',
              icon_letter: 'M',
            }),
          });
          if (created.ok) {
            const p = await created.json();
            setProjects([p]);
            setActiveProjectId(p.project_id);
          }
        }
      }
    } catch { /* silent */ }
  }, [activeProjectId]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    if (!activeProjectId) { setActiveProject(null); return; }
    const p = projects.find(x => x.project_id === activeProjectId);
    setActiveProject(p || null);
  }, [activeProjectId, projects]);

  const fetchThreads = useCallback(async () => {
    if (!activeProjectId) return setThreads([]);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads?project_id=${activeProjectId}`);
      if (r.ok) {
        const data = await r.json();
        setThreads(data.threads || []);
        if (!activeThreadId && data.threads?.length > 0) {
          setActiveThreadId(data.threads[0].thread_id);
        }
      }
    } catch { /* silent */ }
  }, [activeProjectId, activeThreadId]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const fetchMessages = useCallback(async () => {
    if (!activeThreadId) return setMessages([]);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${activeThreadId}/messages`);
      if (r.ok) {
        const data = await r.json();
        setMessages(data.messages || []);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      }
    } catch { /* silent */ }
  }, [activeThreadId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // ── Actions ────
  const handleNewChat = async () => {
    if (!activeProjectId) {
      toast.error('Create or pick a project first.');
      return;
    }
    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'New chat', model: selectedModel, project_id: activeProjectId }),
      });
      if (r.ok) {
        const t = await r.json();
        setActiveThreadId(t.thread_id);
        setMessages([]);
        await fetchThreads();
        composerRef.current?.focus();
      }
    } catch { toast.error('Could not start chat'); }
  };

  const handleSend = async () => {
    const text = composerText.trim();
    if (!text || sending) return;
    if (!activeProjectId) { toast.error('Pick a project first'); return; }

    // Lazy-create a thread if none active
    let threadId = activeThreadId;
    if (!threadId) {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: text.slice(0, 60), model: selectedModel, project_id: activeProjectId }),
      });
      if (!r.ok) { toast.error('Could not start chat'); return; }
      const t = await r.json();
      threadId = t.thread_id;
      setActiveThreadId(threadId);
      await fetchThreads();
    }

    // Optimistic append
    const optimistic = { message_id: `tmp_${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    setComposerText('');
    setSending(true);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    try {
      const r = await apiFetch(`${API_URL}/api/ai-hub/threads/${threadId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text, model: selectedModel }),
      });
      if (r.ok) {
        await fetchMessages();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Message failed');
        setMessages(prev => prev.filter(m => m.message_id !== optimistic.message_id));
      }
    } catch (_e) {
      toast.error('Network error');
    } finally {
      setSending(false);
    }
  };

  // ── Derived ───
  const filteredThreads = useMemo(() => {
    if (!searchTerm) return threads;
    const q = searchTerm.toLowerCase();
    return threads.filter(t => (t.title || '').toLowerCase().includes(q));
  }, [threads, searchTerm]);

  const selectedModelMeta = useMemo(
    () => MODELS.find(m => m.id === selectedModel) || MODELS[0],
    [selectedModel],
  );

  // Group chats by recency for the project's "Recent Chats" sidebar
  const groupedThreads = useMemo(() => {
    const now = new Date();
    const today = [], yesterday = [], lastWeek = [], older = [];
    filteredThreads.forEach(t => {
      const d = new Date(t.updated_at || t.created_at);
      const diffH = (now - d) / (1000 * 60 * 60);
      if (diffH < 24) today.push(t);
      else if (diffH < 48) yesterday.push(t);
      else if (diffH < 24 * 7) lastWeek.push(t);
      else older.push(t);
    });
    return { today, yesterday, lastWeek, older };
  }, [filteredThreads]);

  return (
    <div className="-m-3 sm:-m-4 lg:-m-6 h-[calc(100vh-48px)] sm:h-[calc(100vh-56px)] bg-background text-foreground flex overflow-hidden" data-testid="ai-workspace">
      {/* ── INNER SIDEBAR (Projects, New Chat, user) ─────────────────────── */}
      <aside className="w-[240px] flex-shrink-0 bg-background border-r border-border/60 flex flex-col">
        {/* New Chat */}
        <div className="px-3 pt-3">
          <Button
            onClick={handleNewChat}
            className="w-full justify-start gap-2 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white font-medium h-10 shadow-lg shadow-emerald-500/20"
            data-testid="ai-workspace-new-chat"
          >
            <Plus className="w-4 h-4" />
            New Chat
            <span className="ml-auto text-[10px] bg-foreground/10 px-1.5 py-0.5 rounded">⌘K</span>
          </Button>
        </div>

        {/* Search */}
        <div className="px-3 pt-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/80" />
            <Input
              placeholder="Search chats…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9 bg-muted/40 border-border text-sm placeholder:text-muted-foreground/80 focus-visible:ring-emerald-500"
              data-testid="ai-workspace-search"
            />
          </div>
        </div>

        {/* Projects (only AI-specific nav in the inner sidebar — the global
            CountBucks AppLayout sidebar handles all other navigation). */}
        <div className="px-3 pt-3 pb-2 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between px-1 mb-1.5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium">Projects</p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="w-5 h-5 rounded hover:bg-muted flex items-center justify-center text-muted-foreground/80 hover:text-foreground"
              data-testid="new-project-btn"
              aria-label="New project"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          <ScrollArea className="flex-1 min-h-0">
            <div className="space-y-0.5">
              {projects.map(p => (
                <button
                  key={p.project_id}
                  type="button"
                  onClick={() => { setActiveProjectId(p.project_id); setActiveThreadId(null); }}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                    activeProjectId === p.project_id
                      ? 'bg-accent text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                  )}
                  data-testid={`project-item-${p.project_id}`}
                >
                  <span
                    className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: p.icon_color || '#10b981' }}
                  >
                    {p.icon_letter || p.name?.[0] || 'P'}
                  </span>
                  <span className="flex-1 text-left truncate">{p.name}</span>
                  <span className="text-[10px] text-muted-foreground/80">{p.chat_count || 0}</span>
                </button>
              ))}
              {projects.length === 0 && (
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="w-full text-left text-xs text-muted-foreground/80 italic px-2 py-1.5 hover:text-foreground/80"
                >
                  Create your first project →
                </button>
              )}
            </div>
          </ScrollArea>
        </div>
      </aside>

      {/* ── MAIN WORKSPACE ──────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-14 px-4 flex items-center justify-between border-b border-border/60 bg-background/80 backdrop-blur">
          <div className="flex items-center gap-3">
            {activeProject ? (
              <button
                type="button"
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/40"
                data-testid="active-project-switcher"
              >
                <span
                  className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ backgroundColor: activeProject.icon_color || '#10b981' }}
                >
                  {activeProject.icon_letter || activeProject.name?.[0]}
                </span>
                <span className="text-sm font-medium text-foreground">{activeProject.name}</span>
                <ChevronDown className="w-3 h-3 text-muted-foreground/80" />
              </button>
            ) : (
              <span className="text-sm text-muted-foreground/80">Pick or create a project →</span>
            )}
          </div>

          {/* Model selector */}
          <div className="flex items-center gap-2">
            <ModelSelector value={selectedModel} onChange={setSelectedModel} />
            <Button size="sm" variant="ghost" className="gap-1.5 text-foreground/80 hover:text-foreground hover:bg-card" data-testid="invite-btn">
              <Users className="w-3.5 h-3.5" />
              Invite
            </Button>
            <Button size="sm" variant="ghost" className="gap-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10" data-testid="secure-link-btn">
              <Lock className="w-3.5 h-3.5" />
              Secure Link
            </Button>
            <Button size="icon" variant="ghost" className="w-8 h-8 text-muted-foreground hover:text-foreground" aria-label="Notifications">
              <Bell className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Project hero (shown only when a project is active) */}
        {activeProject ? (
          <>
            <section className="relative">
              {/* Cover */}
              <div
                className="h-44 w-full relative overflow-hidden"
                style={{
                  background: activeProject.cover_image_url
                    ? `url(${activeProject.cover_image_url}) center/cover`
                    : COVER_PRESETS[Math.abs(hashCode(activeProject.project_id || '')) % COVER_PRESETS.length],
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditOpen(true)}
                  className="absolute right-4 bottom-4 bg-card/80 backdrop-blur-sm border border-border text-foreground hover:bg-muted gap-1.5"
                  data-testid="change-cover-btn"
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  Change Cover
                </Button>
              </div>

              {/* Header row overlapping cover */}
              <div className="px-6 -mt-12 pb-3 flex items-end gap-4">
                <div
                  className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-xl shadow-black/40 border-4 border-background flex-shrink-0"
                  style={{ backgroundColor: activeProject.icon_color || '#10b981' }}
                >
                  {activeProject.icon_letter || activeProject.name?.[0]}
                </div>
                <div className="flex-1 pb-2 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">{activeProject.name}</h1>
                    {activeProject.privacy === 'private' ? (
                      <Badge variant="outline" className="border-border text-muted-foreground gap-1 text-[10px]">
                        <Lock className="w-2.5 h-2.5" /> Private
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-emerald-700/50 text-emerald-400 gap-1 text-[10px]">
                        <Shield className="w-2.5 h-2.5" /> {activeProject.privacy}
                      </Badge>
                    )}
                  </div>
                  {activeProject.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{activeProject.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground/80">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {activeProject.member_user_ids?.length || 1} member{(activeProject.member_user_ids?.length || 1) === 1 ? '' : 's'}
                    </span>
                    <span>•</span>
                    <span>{activeProject.chat_count || 0} chats</span>
                    <span>•</span>
                    <span>{activeProject.linked_workspace_ids?.length || 0} workspace{(activeProject.linked_workspace_ids?.length || 0) === 1 ? '' : 's'}</span>
                    <span>•</span>
                    <span>Created {new Date(activeProject.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Button size="sm" variant="ghost" onClick={() => setLinkWsOpen(true)} className="text-foreground/80 hover:text-foreground gap-1.5" data-testid="link-workspaces-btn">
                    <Wallet className="w-3.5 h-3.5" /> {activeProject.linked_workspace_ids?.length > 0 ? 'Workspaces' : 'Link Workspace'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)} className="text-foreground/80 hover:text-foreground gap-1.5" data-testid="edit-project-btn">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button size="icon" variant="ghost" className="w-8 h-8 text-muted-foreground">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Project tabs */}
              <div className="px-6 border-b border-border/60">
                <div className="flex gap-1">
                  {['chats', 'files', 'knowledge', 'team', 'settings'].map(tab => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={cn(
                        'px-3 py-2 text-sm font-medium capitalize flex items-center gap-1.5 border-b-2 transition-colors',
                        activeTab === tab
                          ? 'border-emerald-500 text-foreground'
                          : 'border-transparent text-muted-foreground/80 hover:text-foreground/90',
                      )}
                      data-testid={`project-tab-${tab}`}
                    >
                      {tab === 'chats' && <MessageSquare className="w-3.5 h-3.5" />}
                      {tab === 'files' && <Files className="w-3.5 h-3.5" />}
                      {tab === 'knowledge' && <Library className="w-3.5 h-3.5" />}
                      {tab === 'team' && <Users className="w-3.5 h-3.5" />}
                      {tab === 'settings' && <Settings className="w-3.5 h-3.5" />}
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Project tab body */}
            <div className="flex-1 overflow-hidden flex">
              {/* Chat list pane */}
              {activeTab === 'chats' && (
                <ChatPane
                  groupedThreads={groupedThreads}
                  activeThreadId={activeThreadId}
                  onSelectThread={setActiveThreadId}
                  onNewChat={handleNewChat}
                  messages={messages}
                  composerText={composerText}
                  setComposerText={setComposerText}
                  composerRef={composerRef}
                  messagesEndRef={messagesEndRef}
                  selectedModel={selectedModel}
                  selectedModelMeta={selectedModelMeta}
                  setSelectedModel={setSelectedModel}
                  sending={sending}
                  onSend={handleSend}
                  projectName={activeProject.name}
                  hasLinkedWorkspaces={(activeProject.linked_workspace_ids?.length || 0) > 0}
                  onLinkWorkspaces={() => setLinkWsOpen(true)}
                />
              )}
              {activeTab === 'files' && <ComingSoonPane title="Project Files" desc="Drag-and-drop files, organize, and have AI reference them in chats." testId="project-files-tab" />}
              {activeTab === 'knowledge' && <ComingSoonPane title="Knowledge Base" desc="Upload SOPs, policies, and reference docs. AI auto-cites them." testId="project-knowledge-tab" />}
              {activeTab === 'team' && <ComingSoonPane title="Team Members" desc="Invite teammates to this project with role-based permissions." testId="project-team-tab" />}
              {activeTab === 'settings' && (
                <ProjectSettingsPane
                  project={activeProject}
                  workspaces={workspaces || []}
                  onLinkWorkspaces={() => setLinkWsOpen(true)}
                  onEdit={() => setEditOpen(true)}
                  onDelete={async () => {
                    if (!window.confirm(`Delete "${activeProject.name}"? This cannot be undone.`)) return;
                    const r = await apiFetch(`${API_URL}/api/projects/${activeProject.project_id}`, { method: 'DELETE' });
                    if (r.ok) {
                      toast.success('Project deleted');
                      setActiveProjectId(null);
                      setActiveProject(null);
                      setActiveThreadId(null);
                      setThreads([]);
                      setMessages([]);
                      setActiveTab('chats');
                      await fetchProjects();
                    } else { toast.error('Could not delete'); }
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <EmptyState onCreate={() => setCreateOpen(true)} hasProjects={projects.length > 0} />
        )}
      </main>

      {/* ── RIGHT RAIL ──────────────────────────────────────────────── */}
      <RightRail project={activeProject} threadCount={threads.length} />

      {/* ── Dialogs ─────────────────────────────────────────────────── */}
      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaces={workspaces || []}
        onCreated={(p) => { setActiveProjectId(p.project_id); fetchProjects(); }}
      />
      <EditProjectDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={activeProject}
        onSaved={fetchProjects}
      />
      <LinkWorkspacesDialog
        open={linkWsOpen}
        onOpenChange={setLinkWsOpen}
        project={activeProject}
        workspaces={workspaces || []}
        onSaved={fetchProjects}
      />
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function ModelSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = MODELS.find(m => m.id === value) || MODELS[0];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/40 border border-border hover:bg-muted/60 text-sm"
        data-testid="model-selector-btn"
      >
        <span className={cn('w-2 h-2 rounded-full', selected.dot)} />
        <span className="text-foreground">{selected.label}</span>
        <span className="text-muted-foreground/80 text-[11px]">({selected.vendor})</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground/80" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-72 bg-card border border-border rounded-lg shadow-2xl z-50 overflow-hidden">
            <div className="p-1">
              {MODELS.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { onChange(m.id); setOpen(false); }}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left text-sm hover:bg-muted/60',
                    value === m.id && 'bg-muted/60',
                  )}
                  data-testid={`model-option-${m.id.replace(/\//g, '-')}`}
                >
                  <span className={cn('w-2 h-2 rounded-full flex-shrink-0', m.dot)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground leading-tight">{m.label}</p>
                    <p className="text-[10px] text-muted-foreground/80 leading-tight">{m.vendor}</p>
                  </div>
                  {value === m.id && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ChatPane({
  groupedThreads, activeThreadId, onSelectThread, onNewChat,
  messages, composerText, setComposerText, composerRef, messagesEndRef,
  selectedModel, selectedModelMeta, setSelectedModel, sending, onSend,
  projectName, hasLinkedWorkspaces, onLinkWorkspaces,
}) {
  return (
    <>
      {/* Threads list */}
      <div className="w-[280px] flex-shrink-0 border-r border-border/60 flex flex-col">
        <div className="px-3 py-2.5 flex items-center justify-between border-b border-border/60">
          <p className="text-xs font-medium text-foreground/80">Recent Chats</p>
          <button
            type="button"
            onClick={onNewChat}
            className="w-6 h-6 rounded hover:bg-muted flex items-center justify-center text-muted-foreground/80 hover:text-foreground"
            aria-label="New chat"
            data-testid="chat-pane-new-chat-btn"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-3">
            {Object.entries(groupedThreads).map(([key, items]) => {
              if (items.length === 0) return null;
              const label = key === 'today' ? 'Today' : key === 'yesterday' ? 'Yesterday' : key === 'lastWeek' ? 'Last 7 Days' : 'Older';
              return (
                <div key={key}>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium px-2 mb-1">{label}</p>
                  {items.map(t => (
                    <button
                      key={t.thread_id}
                      type="button"
                      onClick={() => onSelectThread(t.thread_id)}
                      className={cn(
                        'w-full text-left px-2 py-2 rounded-md group flex items-center gap-2 transition-colors',
                        activeThreadId === t.thread_id ? 'bg-accent' : 'hover:bg-muted/40',
                      )}
                      data-testid={`thread-item-${t.thread_id}`}
                    >
                      <FileText className="w-3.5 h-3.5 text-muted-foreground/80 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className={cn('text-[13px] truncate', activeThreadId === t.thread_id ? 'text-foreground' : 'text-foreground/80')}>{t.title}</p>
                      </div>
                      <span className="text-[10px] text-muted-foreground/60">{timeAgo(t.updated_at || t.created_at)}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            {Object.values(groupedThreads).every(g => g.length === 0) && (
              <p className="text-xs text-muted-foreground/60 italic px-2 py-4 text-center">No chats yet. Send a message to start.</p>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Conversation */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1">
          <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/30 items-center justify-center mb-3">
                  <Sparkle className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-1">Ask {projectName} anything</h3>
                <p className="text-sm text-muted-foreground/80 max-w-md mx-auto">
                  {hasLinkedWorkspaces
                    ? 'Your linked workspaces give me real-time access to your financials. Try "What\'s our cash position?" or "Summarize Q2 revenue."'
                    : 'Tip: link a workspace to give me access to your real financial data.'}
                </p>
                {!hasLinkedWorkspaces && (
                  <Button size="sm" variant="outline" onClick={onLinkWorkspaces} className="mt-3 border-border text-foreground/80 hover:text-foreground" data-testid="link-workspace-cta">
                    <Wallet className="w-3.5 h-3.5 mr-1.5" /> Link a workspace
                  </Button>
                )}
              </div>
            ) : messages.map(m => (
              <ChatMessage key={m.message_id} msg={m} modelMeta={selectedModelMeta} />
            ))}
            {sending && (
              <div className="flex items-start gap-3">
                <div className={cn('w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0', selectedModelMeta.dot, 'bg-opacity-20')}>
                  <Bot className="w-4 h-4 text-foreground/80" />
                </div>
                <div className="flex items-center gap-1.5 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground/80" />
                  <span className="text-xs text-muted-foreground/80">{selectedModelMeta.label} is thinking…</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Composer */}
        <div className="border-t border-border/60 p-4">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-xl border border-border bg-muted/40 focus-within:border-emerald-500/50 transition-colors">
              <Textarea
                ref={composerRef}
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                placeholder={`Message ${projectName}…`}
                className="bg-transparent border-0 resize-none min-h-[64px] focus-visible:ring-0 text-sm placeholder:text-muted-foreground/80"
                data-testid="composer-input"
              />
              <div className="flex items-center gap-1 px-2 pb-2">
                <Button size="icon" variant="ghost" className="w-7 h-7 text-muted-foreground/80 hover:text-foreground/90" aria-label="Attach">
                  <Paperclip className="w-3.5 h-3.5" />
                </Button>
                <ModelSelector value={selectedModel} onChange={setSelectedModel} />
                <div className="flex-1" />
                <Button
                  size="sm"
                  onClick={onSend}
                  disabled={!composerText.trim() || sending}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 h-8"
                  data-testid="composer-send"
                >
                  {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  Send
                </Button>
              </div>
            </div>
            {hasLinkedWorkspaces && (
              <p className="text-[11px] text-muted-foreground/60 mt-2 flex items-center gap-1">
                <Activity className="w-3 h-3 text-emerald-500" />
                Financial context is live · AI can see your linked workspaces' cash, AR, AP, and 30-day P&amp;L.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ChatMessage({ msg, modelMeta }) {
  const isUser = msg.role === 'user';
  return (
    <div className={cn('flex items-start gap-3', isUser && 'flex-row-reverse')}>
      <div
        className={cn(
          'w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0',
          isUser ? 'bg-emerald-600 text-white' : 'bg-muted text-foreground/80 border border-border',
        )}
      >
        {isUser ? 'U' : <Bot className="w-4 h-4" />}
      </div>
      <div className={cn('flex-1 min-w-0', isUser && 'flex flex-col items-end')}>
        <div
          className={cn(
            'rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed max-w-[88%]',
            isUser ? 'bg-emerald-600/15 border border-emerald-500/20 text-foreground' : 'text-foreground/90',
          )}
        >
          {msg.content}
        </div>
        <div className="text-[10px] text-muted-foreground/60 mt-1 px-1">
          {isUser ? 'You' : (msg.model || modelMeta?.label || 'Assistant')} · {timeAgo(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

function RightRail({ project, threadCount }) {
  if (!project) {
    return (
      <aside className="w-[280px] flex-shrink-0 border-l border-border/60 bg-background hidden xl:block" />
    );
  }
  return (
    <aside className="w-[280px] flex-shrink-0 border-l border-border/60 bg-background hidden xl:block overflow-y-auto" data-testid="right-rail">
      <div className="p-4 space-y-4">
        {/* Project Overview */}
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium">Project Overview</p>
            <button className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-0.5">
              <Pencil className="w-2.5 h-2.5" /> Edit
            </button>
          </div>
          <dl className="space-y-1.5">
            <DLRow label="Members" value={`${project.member_user_ids?.length || 1}`} />
            <DLRow label="Total Chats" value={threadCount} />
            <DLRow label="Files" value={project.file_count || 0} />
            <DLRow label="Knowledge Docs" value={project.knowledge_doc_count || 0} />
            <DLRow label="Created" value={new Date(project.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
          </dl>
        </div>

        {/* Credits */}
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium">Credits Usage</p>
            <button className="text-[11px] text-emerald-400 hover:text-emerald-300">View Details</button>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#27272a" strokeWidth="3" />
                <circle cx="18" cy="18" r="14" fill="none" stroke="url(#grad)" strokeWidth="3" strokeDasharray={`${0.72 * 88} 88`} strokeLinecap="round" />
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#06b6d4" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-foreground">72%</div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">7,200 / 10,000</p>
              <p className="text-[11px] text-muted-foreground/80">Credits used</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">Resets in 12 days</p>
            </div>
          </div>
        </div>

        {/* Top Models */}
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium mb-3">Top Models</p>
          {[
            { name: 'GPT-5.2', credits: '4,200', pct: 58, color: 'bg-emerald-500' },
            { name: 'Claude Sonnet 4.5', credits: '1,800', pct: 25, color: 'bg-amber-500' },
            { name: 'Gemini 2.5', credits: '900', pct: 12, color: 'bg-blue-500' },
            { name: 'Llama 3.3', credits: '300', pct: 5, color: 'bg-indigo-500' },
          ].map(m => (
            <div key={m.name} className="mb-2 last:mb-0">
              <div className="flex items-center justify-between text-[11px] mb-1">
                <span className="text-foreground/80">{m.name}</span>
                <span className="text-muted-foreground/80">{m.credits} <span className="text-muted-foreground/60">· {m.pct}%</span></span>
              </div>
              <div className="h-1 rounded-full bg-muted overflow-hidden">
                <div className={cn('h-full rounded-full', m.color)} style={{ width: `${m.pct}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Shortcuts */}
        <div className="rounded-xl border border-border bg-muted/30 p-3">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-medium mb-2">Project Shortcuts</p>
          {[
            { icon: Files, label: 'Upload Files' },
            { icon: MessageSquare, label: 'Create New Chat' },
            { icon: Users, label: 'Invite Team Member' },
            { icon: Settings, label: 'Project Settings' },
          ].map(s => {
            const Icon = s.icon;
            return (
              <button key={s.label} type="button" className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-foreground/80 hover:text-foreground hover:bg-muted/60">
                <Icon className="w-3.5 h-3.5" />
                <span className="flex-1 text-left">{s.label}</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground/60" />
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function DLRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[12px]">
      <dt className="text-muted-foreground/80">{label}</dt>
      <dd className="text-foreground font-medium">{value}</dd>
    </div>
  );
}

function EmptyState({ onCreate, hasProjects }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/30 items-center justify-center mb-4">
          <FolderKanban className="w-7 h-7 text-emerald-400" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {hasProjects ? 'Pick a project' : 'Create your first project'}
        </h2>
        <p className="text-sm text-muted-foreground/80 mb-5">
          {hasProjects
            ? 'Pick a project from the sidebar — or create a new one to keep chats, files, and team focused.'
            : 'Projects are AI conversation containers. Each one has its own chats, files, knowledge, and team — and can read live financials from any of your CountBucks workspaces.'}
        </p>
        <Button onClick={onCreate} className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white gap-2" data-testid="empty-state-create-project">
          <Plus className="w-4 h-4" /> New project
        </Button>
      </div>
    </div>
  );
}

function ComingSoonPane({ title, desc, testId }) {
  return (
    <div className="flex-1 flex items-center justify-center" data-testid={testId}>
      <div className="text-center max-w-sm">
        <div className="inline-flex w-12 h-12 rounded-xl bg-card border border-border items-center justify-center mb-3">
          <Sparkles className="w-5 h-5 text-muted-foreground/80" />
        </div>
        <h3 className="text-sm font-semibold text-foreground/80 mb-1">{title}</h3>
        <p className="text-xs text-muted-foreground/80">{desc}</p>
        <Badge variant="outline" className="mt-3 border-border text-muted-foreground/80 text-[10px]">Coming next session</Badge>
      </div>
    </div>
  );
}

function ProjectSettingsPane({ project, workspaces, onLinkWorkspaces, onEdit, onDelete }) {
  const linkedNames = (project?.linked_workspace_ids || [])
    .map(id => workspaces.find(w => w.workspace_id === id)?.name)
    .filter(Boolean);
  return (
    <div className="flex-1 p-6 overflow-y-auto" data-testid="project-settings-tab">
      <div className="max-w-2xl space-y-4">
        <div className="rounded-xl border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">General</h3>
          <Button size="sm" variant="outline" onClick={onEdit} className="border-border text-foreground/80 hover:text-foreground">
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit name, description, icon
          </Button>
        </div>
        <div className="rounded-xl border border-border p-4">
          <h3 className="text-sm font-semibold text-foreground mb-1">Linked workspaces</h3>
          <p className="text-xs text-muted-foreground/80 mb-3">
            Workspaces this project's AI can read financial data from. Link multiple for consolidated visibility.
          </p>
          {linkedNames.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {linkedNames.map(n => (
                <Badge key={n} variant="secondary" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                  <Wallet className="w-3 h-3 mr-1" /> {n}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/80 italic mb-3">No workspaces linked yet.</p>
          )}
          <Button size="sm" variant="outline" onClick={onLinkWorkspaces} className="border-border text-foreground/80 hover:text-foreground">
            <Wallet className="w-3.5 h-3.5 mr-1.5" /> Manage linked workspaces
          </Button>
        </div>
        <div className="rounded-xl border border-red-900/40 bg-red-950/10 p-4">
          <h3 className="text-sm font-semibold text-red-400 mb-1">Danger zone</h3>
          <p className="text-xs text-muted-foreground/80 mb-3">Deletes this project and all its chats. Workspaces stay intact.</p>
          <Button size="sm" variant="outline" onClick={onDelete} className="border-red-900/50 text-red-400 hover:bg-red-950/30 hover:text-red-300" data-testid="delete-project-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete project
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Dialogs ────────────────────────────────────────────────────────────

function CreateProjectDialog({ open, onOpenChange, workspaces, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconColor, setIconColor] = useState('#10b981');
  const [linkedWs, setLinkedWs] = useState(new Set());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) { setName(''); setDescription(''); setIconColor('#10b981'); setLinkedWs(new Set()); }
  }, [open]);

  const palette = ['#10b981', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#64a92e'];

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Project name is required'); return; }
    setCreating(true);
    try {
      const r = await apiFetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim(),
          icon_color: iconColor,
          icon_letter: name.trim()[0]?.toUpperCase(),
          linked_workspace_ids: [...linkedWs],
        }),
      });
      if (r.ok) {
        const p = await r.json();
        toast.success(`Created "${p.name}"`);
        onCreated?.(p);
        onOpenChange(false);
      } else { toast.error('Could not create project'); }
    } catch { toast.error('Could not create project'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background border-border text-foreground" data-testid="create-project-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FolderKanban className="w-4 h-4 text-emerald-400" /> New project</DialogTitle>
          <DialogDescription className="text-muted-foreground/80">A container for chats, files, and team — optionally tied to one or more workspaces for live financials.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Accounting" className="bg-card border-border" data-testid="create-project-name" autoFocus />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Description (optional)</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's this project about?" className="bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Icon color</label>
            <div className="flex gap-1.5">
              {palette.map(c => (
                <button key={c} type="button" onClick={() => setIconColor(c)} className={cn('w-7 h-7 rounded-md transition-all', iconColor === c && 'ring-2 ring-offset-2 ring-offset-background ring-foreground/40')} style={{ backgroundColor: c }} aria-label={c} />
              ))}
            </div>
          </div>
          {workspaces.length > 0 && (
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Link workspaces (optional)</label>
              <p className="text-[11px] text-muted-foreground/60 mb-2">AI can read financial data from the workspaces you link.</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {workspaces.map(w => (
                  <label key={w.workspace_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-card cursor-pointer">
                    <Checkbox
                      checked={linkedWs.has(w.workspace_id)}
                      onCheckedChange={() => {
                        const n = new Set(linkedWs);
                        n.has(w.workspace_id) ? n.delete(w.workspace_id) : n.add(w.workspace_id);
                        setLinkedWs(n);
                      }}
                    />
                    <span className="text-sm text-foreground/90">{w.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          <Button onClick={handleCreate} disabled={creating || !name.trim()} className="bg-emerald-600 hover:bg-emerald-500" data-testid="create-project-submit">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProjectDialog({ open, onOpenChange, project, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconColor, setIconColor] = useState('#10b981');
  const [coverUrl, setCoverUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && project) {
      setName(project.name || '');
      setDescription(project.description || '');
      setIconColor(project.icon_color || '#10b981');
      setCoverUrl(project.cover_image_url || '');
    }
  }, [open, project]);

  const palette = ['#10b981', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#64a92e'];

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/projects/${project.project_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description, icon_color: iconColor, cover_image_url: coverUrl || null }),
      });
      if (r.ok) { toast.success('Saved'); onSaved?.(); onOpenChange(false); } else { toast.error('Could not save'); }
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  if (!project) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background border-border text-foreground" data-testid="edit-project-dialog">
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Icon color</label>
            <div className="flex gap-1.5">
              {palette.map(c => (
                <button key={c} type="button" onClick={() => setIconColor(c)} className={cn('w-7 h-7 rounded-md', iconColor === c && 'ring-2 ring-offset-2 ring-offset-background ring-foreground/40')} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Cover image URL (optional)</label>
            <Input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="https://..." className="bg-card border-border" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="edit-project-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkWorkspacesDialog({ open, onOpenChange, project, workspaces, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && project) setSelected(new Set(project.linked_workspace_ids || []));
  }, [open, project]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/projects/${project.project_id}/link-workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linked_workspace_ids: [...selected] }),
      });
      if (r.ok) { toast.success('Workspaces linked'); onSaved?.(); onOpenChange(false); } else { toast.error('Could not link'); }
    } catch { toast.error('Could not link'); }
    finally { setSaving(false); }
  };

  if (!project) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background border-border text-foreground" data-testid="link-workspaces-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400" /> Link workspaces</DialogTitle>
          <DialogDescription className="text-muted-foreground/80">
            Pick which CountBucks workspaces this project's AI can read financial data from. You can link more than one for consolidated visibility.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {workspaces.length === 0 ? (
            <p className="text-sm text-muted-foreground/80 py-4 text-center">No workspaces available.</p>
          ) : workspaces.map(w => (
            <label key={w.workspace_id} className="flex items-center gap-2.5 p-2 rounded hover:bg-card cursor-pointer" data-testid={`ws-checkbox-${w.workspace_id}`}>
              <Checkbox
                checked={selected.has(w.workspace_id)}
                onCheckedChange={() => {
                  const n = new Set(selected);
                  n.has(w.workspace_id) ? n.delete(w.workspace_id) : n.add(w.workspace_id);
                  setSelected(n);
                }}
              />
              <div className="w-8 h-8 rounded bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                {w.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground/90">{w.name}</p>
                {w.industry && <p className="text-[11px] text-muted-foreground/80">{w.industry}</p>}
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="link-workspaces-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
            Save links
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Tiny stable hash for cover preset selection
function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
}
